import { useMutation } from "@apollo/client/react";
import { DSButton } from "@components/design-system/molecules";
import { APPOINTMENT_OBJECTIVE_NORMAL_DEFAULTS } from "@scribenote/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FormProvider } from "react-hook-form";

import { EDIT_OBJECTIVE_NORMAL_DEFAULTS } from "../../../graphql/mutations/ObjectiveNormalDefaults";
import { alert } from "../../common/Alert";
import ObjectiveFieldItem from "../../notes/legacy/LegacyObjectiveFieldItem";
import StepWrapper from "../StepWrapper";

export default function CodSetupStep({ formMethods }) {
  const { control, getValues, setValue } = formMethods;
  const { useGptUnstableObjectiveDefaults } = useFlags();

  const [editObjectiveNormalDefaults] = useMutation(
    EDIT_OBJECTIVE_NORMAL_DEFAULTS,
  );

  const handleSaveDefaults = async () => {
    const formValues = getValues();
    const structuredObjectiveNormalDefaults = {};
    formValues?.objectiveNormalDefaultsFieldArray?.forEach(
      (field) => {
        structuredObjectiveNormalDefaults[field.name] = field.value;
      },
    );

    const hasValues = Object.values(
      structuredObjectiveNormalDefaults,
    ).some((value) => value && value.trim() !== "");

    try {
      await editObjectiveNormalDefaults({
        variables: {
          objectiveNormalDefaultsInput: {
            shouldUse: hasValues,
            ...structuredObjectiveNormalDefaults,
          },
        },
      });

      alert(
        "success",
        "Custom objective defaults saved successfully!",
      );
    } catch (error) {
      console.error("Error saving defaults:", error);

      alert(
        "error",
        "Failed to save custom objective defaults. Please try again.",
      );
    }
  };

  return (
    <StepWrapper
      title="Set up your Custom Objective Defaults"
      description="These will be used in Scribenote's default SOAPs when you don't verbalize details for a system. For example, if you don't mention the cardiovascular system during a consultation, it will use your default phrase instead of 'Normal on Exam'."
      withBackground={false}
      maxWidth="max-w-[90vw]"
    >
      <FormProvider {...formMethods}>
        <div className="bg-white/10 rounded-xl p-4 border border-white/20 shadow-lg backdrop-blur-md">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-4">
            {APPOINTMENT_OBJECTIVE_NORMAL_DEFAULTS.map(
              (field, index) => {
                const excludedFields = [
                  "nasal",
                  "masses",
                  "mucousMembranes",
                ];
                if (
                  excludedFields.includes(field.name) &&
                  !useGptUnstableObjectiveDefaults
                ) {
                  return null;
                }
                return (
                  <ObjectiveFieldItem
                    key={field.name}
                    fieldName={field.name}
                    fieldLabel={field.label}
                    formFieldValue={`objectiveNormalDefaultsFieldArray[${index}].value`}
                    control={control}
                    isForSOAPForm={false}
                    isForOnboarding={true}
                    placeholder={"Normal on Exam"}
                    setValue={setValue}
                  />
                );
              },
            )}
            <div className="flex mt-1 justify-center">
              <DSButton
                onClick={handleSaveDefaults}
                variant="primary"
                dataTestId="save-defaults-button"
              >
                Save Defaults
              </DSButton>
            </div>
          </div>
        </div>
      </FormProvider>
    </StepWrapper>
  );
}
